import { Button, EmptyState, Select, Table, type TableColumn } from '@bennows/boows-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime } from '../../lib/format';
import type { OperatorMe } from '../auth/useOperatorMe';
import { useOperators } from '../operators/useOperators';
import { useAuditActionLabel } from './useAuditActionLabel';
import { AUDIT_ACTIONS, useAuditLog, type AuditFilter, type LogEvent } from './useAuditLog';

const CARD_BREAKPOINT = 640;
/** Select option for "no filter"; the kit's Select treats an empty id as no choice. */
const ALL = 'all';
const NO_FILTER: AuditFilter = { action: '', operatorId: '' };

/** The operator log across all tenants: who did what, with which reason (REQ-140-005). */
export function AuditLogPage() {
  const { t } = useTranslation();
  const me = useOutletContext<OperatorMe>();
  const [filter, setFilter] = useState<AuditFilter>(NO_FILTER);
  const log = useAuditLog(filter);
  const actionLabel = useAuditActionLabel();
  // Only operator managers may list the operators; the others filter by action alone.
  const canListOperators = me.permissions.includes('operators:manage');
  const operators = useOperators({ enabled: canListOperators });
  const rows = log.data?.pages.flatMap((page) => page.data) ?? [];
  const filtered = filter.action !== '' || filter.operatorId !== '';

  const who = (row: LogEvent) => row.operator?.display_name ?? t('tenant.audit.system');
  const tenant = (row: LogEvent) =>
    row.tenant ? (
      <Link className="ui-console-name__link" to={`/tenants/${row.tenant.id}`}>
        {row.tenant.name}
      </Link>
    ) : (
      <span className="ui-console-muted">{t('auditLog.noTenant')}</span>
    );
  const columns: TableColumn<LogEvent>[] = [
    { key: 'when', header: t('tenant.audit.when'), cell: (row) => formatDateTime(row.created_at) },
    { key: 'who', header: t('tenant.audit.who'), cell: who },
    { key: 'action', header: t('tenant.audit.action'), cell: (row) => actionLabel(row.action) },
    { key: 'tenant', header: t('auditLog.tenant'), cell: tenant },
    { key: 'reason', header: t('tenant.audit.reason'), cell: (row) => row.reason ?? t('tenant.audit.noReason') },
  ];

  return (
    <div className="ui-stack">
      <PageHeader title={t('auditLog.title')} description={t('auditLog.description')} />
      <div className="ui-console-toolbar">
        <Select
          hideLabel
          label={t('auditLog.action')}
          value={filter.action || ALL}
          options={[{ id: ALL, label: t('auditLog.allActions') }, ...AUDIT_ACTIONS.map((id) => ({ id, label: actionLabel(id) }))]}
          onChange={(id) => setFilter((current) => ({ ...current, action: id === ALL ? '' : id }))}
        />
        {canListOperators ? (
          <Select
            hideLabel
            label={t('auditLog.operator')}
            value={filter.operatorId || ALL}
            options={[{ id: ALL, label: t('auditLog.allOperators') }, ...(operators.data?.items ?? []).map((entry) => ({ id: entry.id, label: entry.display_name }))]}
            onChange={(id) => setFilter((current) => ({ ...current, operatorId: id === ALL ? '' : id }))}
          />
        ) : null}
      </div>
      {log.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('tenant.audit.loadError')}</p>
          <Button variant="secondary" onClick={() => void log.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <>
          <Table
            ariaLabel={t('auditLog.listLabel')}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={log.isPending}
            emptyState={
              filtered ? (
                <EmptyState
                  title={t('auditLog.emptyFiltered')}
                  action={
                    <Button variant="secondary" onClick={() => setFilter(NO_FILTER)}>
                      {t('tenants.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState title={t('tenant.audit.empty')} />
              )
            }
            labels={{ empty: t(filtered ? 'auditLog.emptyFiltered' : 'tenant.audit.empty') }}
            cardBreakpoint={CARD_BREAKPOINT}
            viewToggle={false}
            mobileViewToggle={false}
            renderCard={(row) => (
              <div className="ui-console-card">
                <strong>{actionLabel(row.action)}</strong>
                <span className="ui-console-muted">
                  {formatDateTime(row.created_at)} · {who(row)}
                </span>
                {row.tenant ? tenant(row) : null}
                {row.reason ? <span>{row.reason}</span> : null}
              </div>
            )}
          />
          {rows.length > 0 ? (
            <div className="ui-console-more">
              <span className="ui-console-muted" aria-live="polite">
                {t('tenant.audit.count', { count: rows.length })}
              </span>
              {log.hasNextPage ? (
                <Button variant="secondary" loading={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
                  {t('tenant.audit.more')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
